/**
 * Privacy orchestrator.
 *
 * Owns the end-to-end privacy lifecycle: manual overrides, CCPA opt-out
 * signaling, existing CMP detection, InMobi bootstrap, and ongoing TCF updates.
 *
 * Sealed bundles run on pages we do not own, so they read the page's CMP and
 * never bootstrap or advertise one of their own — the InMobi branch is compiled
 * out of them.
 */

import type { CmpInfo } from '../../core/config/types';
import { readGppApi, readTcfApi, readUspApi } from './cmpApis';
import { ConsentEngine, type LoadCmpMode } from '../../core/consent/engine';
import type { ConsentInit, PrivacyApi, PrivacyInit } from '../../core/consent/port';
import { loadInMobiChoice } from './inmobi';
import { initTcfBoilerplate } from './tcfBoilerplate';
import { subscribeTcfConsent, waitForTcf } from './tcf';
import { readUsp } from './usp';
import { debug } from '../../util/log';
import { createPrivacyApi, isCcpaOptedOut } from './optout';

const CMP_API_WAIT_MS = 60_000;
const CMP_API_POLL_MS = 100;
const EXISTING_CMP_TCF_WAIT_MS = 8000;
/**
 * How long the page's own CMP gets to register `__tcfapi` before we call it
 * absent. A publisher CMP often loads after us, and deciding on the
 * synchronous check alone would deny a visitor who is about to be asked.
 */
const PAGE_CMP_WAIT_MS = 3000;

export interface PrivacyManagerOptions {
  rootDomain: string;
  cmp: CmpInfo;
  loadCmp: LoadCmpMode;
  consentOverride?: ConsentInit;
  privacyInit?: PrivacyInit;
  /** The kernel's engine. Omitted only by tests, which drive a manager standalone. */
  engine?: ConsentEngine;
}

export class PrivacyManager {
  readonly engine: ConsentEngine;
  readonly privacy: PrivacyApi;

  private tcfSubscribed = false;
  private destroyed = false;
  private unsubscribeTcf: (() => void) | null = null;
  private cmpReadyResolved = false;
  private cmpReadyPromise: Promise<void> | null = null;
  private resolveCmpReady: (() => void) | null = null;

  constructor(private readonly opts: PrivacyManagerOptions) {
    this.engine = opts.engine ?? new ConsentEngine();
    this.privacy = createPrivacyApi({
      rootDomain: opts.rootDomain,
      cmp: opts.cmp,
      consent: this.engine,
    });
  }

  async start(): Promise<void> {
    this.applyPrivacyInit();

    if (this.opts.consentOverride) {
      this.engine.setOverride(this.opts.consentOverride);
      return;
    }

    if (this.opts.cmp.required) {
      debug('privacy: opt-in regime starts denied');
      this.engine.setResolved('denied');
      void this.resolveFromCmpAsync();
      return;
    }

    const resolved = await this.resolveCurrentConsent();
    this.engine.setResolved(resolved);
  }

  destroy(): void {
    this.destroyed = true;
    this.unsubscribeTcf?.();
    this.unsubscribeTcf = null;
    this.tcfSubscribed = false;
    this.cmpReadyResolved = false;
    this.cmpReadyPromise = null;
    this.resolveCmpReady = null;
  }

  /** Yoto parity: defer session_init / pv until first CMP TCF response when opt-in. */
  shouldDeferAnalyticsLifecycle(): boolean {
    if (this.opts.consentOverride) return false;
    return this.opts.cmp.required;
  }

  waitForFirstCmpEvent(): Promise<void> {
    if (!this.shouldDeferAnalyticsLifecycle()) return Promise.resolve();
    if (this.cmpReadyResolved) return Promise.resolve();
    if (!this.cmpReadyPromise) {
      this.cmpReadyPromise = new Promise<void>((resolve) => {
        this.resolveCmpReady = resolve;
      });
    }
    return this.cmpReadyPromise;
  }

  private notifyCmpReady(): void {
    if (this.cmpReadyResolved) return;
    this.cmpReadyResolved = true;
    const resolve = this.resolveCmpReady;
    this.resolveCmpReady = null;
    resolve?.();
  }

  private applyPrivacyInit(): void {
    if (!this.opts.privacyInit || !this.opts.cmp.ccpa_applicable) return;
    if (this.opts.privacyInit === 'opted-out') {
      this.privacy.optOut();
    } else {
      this.privacy.optIn();
    }
  }

  private async resolveFromCmpAsync(): Promise<void> {
    try {
      const resolved = await this.resolveCurrentConsent();
      this.engine.setResolved(resolved);
    } finally {
      this.notifyCmpReady();
    }
  }

  private async resolveCurrentConsent(): Promise<'granted' | 'denied'> {
    if (__SEALED__ && this.opts.cmp.required && !hasCmpApi()) {
      await waitForApi(hasCmpApi, PAGE_CMP_WAIT_MS, CMP_API_POLL_MS);
    }

    if (hasCmpApi()) {
      debug('privacy: detected existing CMP', describeCmpApis());
      const resolved = await this.readFromExistingCmp(EXISTING_CMP_TCF_WAIT_MS);
      return this.applyCcpaVeto(resolved);
    }

    if (this.opts.cmp.required) {
      // A sealed bundle is a guest on a page we do not own: it reads the
      // publisher's CMP and never adds one. No consent signal means the SDK
      // does not run at all — `core/init.ts` halts before anything installs —
      // so the branch below is compiled out of sealed bundles entirely.
      if (!__SEALED__ && this.opts.loadCmp === 'auto') {
        debug('privacy: loading InMobi CMP');
        loadInMobiChoice();
        initTcfBoilerplate();
        await waitForApi(() => !!readTcfApi(), CMP_API_WAIT_MS, CMP_API_POLL_MS);
        const resolved = await this.readFromExistingCmp(CMP_API_WAIT_MS);
        return this.applyCcpaVeto(resolved);
      }
      debug('privacy: no CMP to read in opt-in regime -> denied');
      this.watchForLateCmp();
      return 'denied';
    }

    return this.applyCcpaVeto('granted');
  }

  /**
   * A CMP that appears after we gave up still gets to change the answer: the
   * subscription it feeds is what turns a later grant into a live SDK. The
   * watch lasts `CMP_API_WAIT_MS` — a CMP that has not registered a minute in
   * is not coming, and an unbounded poll would outlive the page's interest.
   */
  private watchForLateCmp(): void {
    if (this.tcfSubscribed) return;
    void waitForApi(() => !!readTcfApi(), CMP_API_WAIT_MS, CMP_API_POLL_MS).then((appeared) => {
      if (!appeared || this.destroyed) return;
      debug('privacy: CMP appeared late — subscribing');
      this.ensureTcfSubscription();
    });
  }

  private async readFromExistingCmp(tcfTimeoutMs: number): Promise<'granted' | 'denied'> {
    if (readTcfApi()) {
      this.ensureTcfSubscription();
      const tcf = await waitForTcf(tcfTimeoutMs);
      if (tcf.kind !== 'unavailable') return tcf.kind;
    }

    if (readUspApi()) {
      const usp = await readUsp();
      if (usp.kind !== 'unavailable') return usp.kind;
    }

    if (this.opts.cmp.required) return 'denied';
    return 'granted';
  }

  private ensureTcfSubscription(): void {
    if (this.tcfSubscribed) return;
    this.tcfSubscribed = true;
    this.unsubscribeTcf = subscribeTcfConsent(
      (status) => {
        this.engine.setResolved(this.applyCcpaVeto(status));
      },
      () => {
        this.notifyCmpReady();
      },
    );
  }

  private applyCcpaVeto(status: 'granted' | 'denied'): 'granted' | 'denied' {
    if (status === 'granted' && this.opts.cmp.ccpa_applicable && isCcpaOptedOut()) {
      return 'denied';
    }
    return status;
  }
}

function hasCmpApi(): boolean {
  return !!readTcfApi() || !!readUspApi() || !!readGppApi();
}

function describeCmpApis(): { hasTcf: boolean; hasUsp: boolean; hasGpp: boolean } {
  return {
    hasTcf: !!readTcfApi(),
    hasUsp: !!readUspApi(),
    hasGpp: !!readGppApi(),
  };
}

function waitForApi(predicate: () => boolean, timeoutMs: number, intervalMs: number): Promise<boolean> {
  return new Promise((resolve) => {
    if (predicate()) {
      resolve(true);
      return;
    }

    const startedAt = Date.now();
    const interval = window.setInterval(() => {
      if (predicate()) {
        window.clearInterval(interval);
        resolve(true);
        return;
      }
      if (Date.now() - startedAt >= timeoutMs) {
        window.clearInterval(interval);
        debug('privacy: CMP API wait timed out', { timeoutMs });
        resolve(false);
      }
    }, intervalMs);
  });
}
